/**
 * SceneTypeChooserModal — Inclusive ContentFocus × DeliveryModality picker.
 *
 * Used by Content Studio "+ New Scene" to filter compatible scene types
 * before opening CreateSceneModal. Falls back to a static YAML-shaped
 * config when the API is unavailable; "Browse all types" always shows
 * the domain catalog unfiltered.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { sceneAPI } from '../../services/api';
import {
  FALLBACK_CHOOSER_CONFIG,
  compatibleSceneTypeIds,
  filterSceneTypes,
  normalizeChooserConfig,
  optionsWithDomainOverlap,
} from './sceneTypeChooser';

const selectStyle = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '8px 10px',
  border: '1px solid var(--coach-border)',
  borderRadius: 6,
  background: 'var(--coach-input-bg)',
  color: 'var(--coach-text)',
  fontSize: 13,
};

export default function SceneTypeChooserModal({
  sceneTypes = [],
  onSelect,
  onClose,
}) {
  const [config, setConfig] = useState(FALLBACK_CHOOSER_CONFIG);
  const [configSource, setConfigSource] = useState('fallback');
  const [focusId, setFocusId] = useState('');
  const [modalityId, setModalityId] = useState('');
  const [browseAll, setBrowseAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    sceneAPI
      .getSceneTypeChooserConfig()
      .then(({ data }) => {
        if (cancelled) return;
        const normalized = normalizeChooserConfig(data?.chooser || data);
        if (normalized) {
          setConfig(normalized);
          setConfigSource(data?.source || 'api');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConfig(FALLBACK_CHOOSER_CONFIG);
          setConfigSource('fallback');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const focusOptions = useMemo(
    () => optionsWithDomainOverlap(config.content_focus, sceneTypes),
    [config, sceneTypes],
  );
  const modalityOptions = useMemo(
    () => optionsWithDomainOverlap(config.delivery_modality, sceneTypes),
    [config, sceneTypes],
  );

  const compatibleIds = useMemo(
    () => compatibleSceneTypeIds(config, focusId || null, modalityId || null, browseAll),
    [config, focusId, modalityId, browseAll],
  );

  const filteredTypes = useMemo(
    () => filterSceneTypes(sceneTypes, compatibleIds),
    [sceneTypes, compatibleIds],
  );

  const selectedFocus = focusOptions.find((f) => f.id === focusId);
  const selectedModality = modalityOptions.find((m) => m.id === modalityId);
  const showingFiltered = !browseAll && (focusId || modalityId);

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="presentation"
      data-testid="scene-type-chooser-backdrop"
    >
      <div
        className="modal-card scene-type-chooser-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="scene-type-chooser-title"
        data-testid="scene-type-chooser-modal"
        data-config-source={configSource}
      >
        <h3 id="scene-type-chooser-title">New Scene</h3>
        <p className="scene-type-chooser-lead">
          Choose what learners should achieve and how you want to deliver it.
          Compatible scene types for this domain appear below.
        </p>

        <div className="scene-type-chooser-pickers">
          <label>
            Content focus
            <select
              style={selectStyle}
              value={focusId}
              disabled={browseAll}
              onChange={(e) => {
                setFocusId(e.target.value);
                setBrowseAll(false);
              }}
              data-testid="chooser-content-focus"
              aria-label="Content focus"
            >
              <option value="">Any focus</option>
              {focusOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.icon ? `${f.icon} ` : ''}
                  {f.label}
                </option>
              ))}
            </select>
            {selectedFocus?.description && (
              <span className="scene-type-chooser-hint">{selectedFocus.description}</span>
            )}
          </label>

          <label>
            Delivery modality
            <select
              style={selectStyle}
              value={modalityId}
              disabled={browseAll}
              onChange={(e) => {
                setModalityId(e.target.value);
                setBrowseAll(false);
              }}
              data-testid="chooser-delivery-modality"
              aria-label="Delivery modality"
            >
              <option value="">Any modality</option>
              {modalityOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.icon ? `${m.icon} ` : ''}
                  {m.label}
                </option>
              ))}
            </select>
            {selectedModality?.description && (
              <span className="scene-type-chooser-hint">{selectedModality.description}</span>
            )}
          </label>
        </div>

        <div className="scene-type-chooser-toolbar">
          <span className="scene-type-chooser-count" data-testid="chooser-type-count">
            {showingFiltered
              ? `${filteredTypes.length} compatible type${filteredTypes.length === 1 ? '' : 's'}`
              : `${filteredTypes.length} scene type${filteredTypes.length === 1 ? '' : 's'}`}
          </span>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => {
              setBrowseAll(true);
              setFocusId('');
              setModalityId('');
            }}
            data-testid="chooser-browse-all"
            aria-pressed={browseAll}
          >
            Browse all types
          </button>
        </div>

        <div
          className="scene-type-chooser-list"
          role="listbox"
          aria-label="Compatible scene types"
          data-testid="chooser-scene-type-list"
        >
          {filteredTypes.length === 0 ? (
            <div className="scene-type-chooser-empty" data-testid="chooser-empty">
              No scene types match this focus and modality for this domain.
              Try another combination or browse all types.
            </div>
          ) : (
            filteredTypes.map((t) => (
              <button
                key={t.id}
                type="button"
                role="option"
                className="scene-type-chooser-item"
                onClick={() => onSelect(t.id)}
                data-testid={`new-scene-${t.id}`}
                title={t.description || ''}
              >
                <span className="scene-type-chooser-item-label">
                  {t.label || t.display_name || t.id}
                </span>
                {t.description ? (
                  <span className="scene-type-chooser-item-desc">
                    {t.description.slice(0, 120)}
                    {t.description.length > 120 ? '…' : ''}
                  </span>
                ) : null}
              </button>
            ))
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
